import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import moment from "moment";
import { map, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { LocaleService } from "../../../core/services/locale.service";
import { QuartersEngine } from "../../../core/utils/quarters-engine";
import { showNotification } from "../../../core/utils/toast";
import {
  AgreementDashboard,
  AgreementData,
  AgreementHeader,
  AgreementResponse,
  EditAgreementHeader,
  GridRow,
  HistoricalSmarts,
  SaveAgreementDTO,
  SmartCell,
  Week,
  Workload,
} from "../../../domain/models/agreement.model";
import { PlanningWizardDTO } from "../../../domain/models/planning-wizard.model";
import { AgreementRepository } from "../../../domain/repositories/agreement.repository";

@Injectable()
export class AgreementRepositoryImpl extends AgreementRepository {
  private readonly endpoint: string = environment.apiUrl;

  private localeService = inject(LocaleService);
  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    super();
  }

  override updatePETC(
    agreementHeader: EditAgreementHeader,
  ): Observable<boolean> {
    let { id, ...agreementInfo } = agreementHeader;
    return this.http
      .put<AgreementResponse>(
        this.endpoint + `/sm/agreement/${agreementHeader.id}/workload-header`,
        { changePeTcDto: agreementInfo },
      )
      .pipe(map((res) => (res.agreement ? true : false)));
  }

  override editNotes(id: number, note: string): Observable<string> {
    return this.http
      .put<{
        note: string;
      }>(this.endpoint + `/sm/agreement/${id}/note`, { note })
      .pipe(map((res) => res.note));
  }

  override save(smartsToCreate: SaveAgreementDTO[]): Observable<boolean> {
    return this.http
      .post<any>(
        this.endpoint + `/sm/agreement/${smartsToCreate[0].agreementId}/smarts`,
        {
          smartDto: smartsToCreate,
        },
      )
      .pipe(map((res) => (res ? true : false)));
  }

  override loadAgreement(
    agreementId: number,
    level: number,
  ): Observable<AgreementData> {
    const isWorkloadOrProposal = [0, 1, 6].includes(level);
    const isPreStaffingOrContract = [2, 3].includes(level);
    return this.http
      .get<AgreementResponse>(
        this.endpoint + `/sm/agreement/${agreementId}/level/${level}`,
      )
      .pipe(
        map((response: AgreementResponse) => {
          try {
            let gridRows: GridRow[] = [];
            let historicalSmarts = response.agreement.historicalSmarts;
            let costFreeze = response.agreement.costFreeze;
            let forecasts = response.agreement.forecasts;
            let quoteAmountLabs = response.agreement.proposal?.quoteAmountLabs;
            let weeksOfProject: Week[] = response.agreement.weeks;
            let today = moment().utc(true);
            today.subtract(2, "weeks");

            let mondayOfTwoWeeksAgo = QuartersEngine.findMondayMoment(today);
            mondayOfTwoWeeksAgo.subtract(1, "day");
            let filteredWeeksId = response.agreement.contract
              ? weeksOfProject
                  .filter((w) =>
                    moment.utc(w.dateStart).isSameOrAfter(mondayOfTwoWeeksAgo),
                  )
                  .map((w) => w.id)
              : weeksOfProject.map((w) => w.id);
            let document = response.agreement.contract
              ? response.agreement.contract
              : response.agreement.proposal
                ? response.agreement.proposal
                : response.agreement.workload!;
            let workload: Workload = response.agreement.workload!;
            const pepResource = workload.pepResource;
            const pepResourceFullname =
              pepResource.name + " " + pepResource.surname;
            const pepResourceId = pepResource.id;
            let SMARTS = response.agreement.smarts;

            let HOURASSIGNMENTFPGEO = response.agreement.hourAssignmentFpGeos;
            if (SMARTS.length > 0) {
              const pepSmarts = SMARTS.filter((smart) => {
                return smart.resource?.id == pepResourceId;
              }).sort((sm1, sm2) => (sm1.week.id > sm2.week.id ? 1 : -1));

              const firstSmart = pepSmarts.length > 0 ? pepSmarts[0] : SMARTS[0];

              const pepDisciplineId = firstSmart.discipline?.id;
              const pepPracticeId = firstSmart.discipline?.practice.id;
              const pepResourceArea =
                    firstSmart.discipline?.practice.description +
                    "/" +
                    firstSmart.discipline?.description;

              gridRows.push({
                type: "area",
                resource: "",
                disciplineId: pepDisciplineId,
                practiceId: pepPracticeId,
                area: pepResourceArea,
                smarts: response.agreement.middleTotals
                  .filter(
                    (mt) =>
                      mt.disciplineId == pepDisciplineId &&
                      mt.professionalFigureId == null &&
                      mt.geoId == null &&
                      filteredWeeksId.includes(mt.weekId),
                  )
                  .map(
                    (totals) =>
                      ({
                        isEdited: false,
                        weeklyContractHours:
                          totals.weeklyContractScheduledHours,
                        scheduledHours: 0,
                        weeklyProposalHours:
                          totals.weeklyProposalScheduledHours,
                        weeklyTheoreticalHours: totals.weeklyTheoreticalHours,
                        week: weeksOfProject.find(
                          (w) => w.id == totals.weekId,
                        )!,
                      }) as SmartCell,
                  ),
                readonly: true,
                hc: 0,
                geoId: "",
                figureId: "",
                figure: "",
                hours: {
                  scheduled: 0,
                  tot: 0,
                  consumed: 0,
                  remaining: 0,
                },
                isPep: true,
              });

              let gPepFig = {
                ...gridRows[0],
                type: "figure",
                figure:
                  firstSmart.professionalFigure?.description +
                  " " +
                  firstSmart.geoId,
                geoId: firstSmart.geoId,
                figureId: firstSmart.professionalFigureId,
                smarts: isWorkloadOrProposal
                  ? pepSmarts.map(
                      (smart) =>
                        ({
                          week: smart.week,
                          weeklyProposalHours:
                            smart.weeklyProposalScheduledHours,
                          scheduledHours: smart.numberOfHours,
                          weeklyTheoreticalHours: smart.weeklyTheoreticalHours,
                          weeklyContractHours:
                            smart.weeklyContractScheduledHours,
                          isEdited: false,
                        }) as SmartCell,
                    )
                  : response.agreement.middleTotals
                      .filter(
                        (mt) =>
                          mt.disciplineId == pepDisciplineId &&
                          mt.professionalFigureId ==
                            firstSmart.professionalFigureId &&
                          mt.geoId == firstSmart.geoId &&
                          mt.weekId >= firstSmart.week.id &&
                          filteredWeeksId.includes(mt.weekId),
                      )
                      .map(
                        (totals) =>
                          ({
                            isEdited: false,
                            weeklyContractHours:
                              totals.weeklyContractScheduledHours,
                            scheduledHours: 0,
                            weeklyProposalHours:
                              totals.weeklyProposalScheduledHours,
                            weeklyTheoreticalHours:
                              totals.weeklyTheoreticalHours,
                            week: weeksOfProject.find(
                              (w) => w.id == totals.weekId,
                            )!,
                          }) as SmartCell,
                      ),
                readonly: isPreStaffingOrContract,
                hours: {
                  // tot: getHourAssignment(HOURASSIGNMENTFPGEO, firstSmart.professionalFigureId, firstSmart.geoId).numberOfHours,
                  tot: 0,
                  scheduled: 0,
                  consumed: 0,
                  remaining: 0,
                },
                hc: 0,
                isPep: true,
              };

              if (!isPreStaffingOrContract) {
                gPepFig.resource = pepResourceFullname;
                gPepFig.resourceId = pepResourceId;
              }
              gridRows.push(gPepFig);
              if (isPreStaffingOrContract) {
                gridRows.push({
                  ...gridRows[1],
                  type: "resource",
                  resource: pepResourceFullname,
                  resourceId: pepResourceId,
                  readonly: false,
                  hours: { tot: 0, scheduled: 0, consumed: 0, remaining: 0 },
                  isPep: true,
                  smarts: pepSmarts.map(
                    (smart) =>
                      ({
                        week: smart.week,
                        weeklyProposalHours: smart.weeklyProposalScheduledHours,
                        scheduledHours: smart.numberOfHours,
                        weeklyTheoreticalHours: smart.weeklyTheoreticalHours,
                        weeklyContractHours: smart.weeklyContractScheduledHours,
                        isEdited: false,
                      }) as SmartCell,
                  ),
                });
              }
              // Rimuovo tutti gli smart relativi al pep dato che li ho gia letti
              SMARTS = [
                ...SMARTS.filter((smart) => {
                  if (smart.resource) {
                    return smart.resource.id != pepResourceId;
                  } else {
                    return true;
                  }
                }),
              ];

              let createResource: boolean = false;
              let createFigure: boolean = false;
              let createArea: boolean = false;

              while (SMARTS.length > 0) {
                let figureSmarts: SmartCell[] = [];
                let areaSmarts: SmartCell[] = [];

                let currentSmart = SMARTS[0];

                if (isPreStaffingOrContract && !!currentSmart.resource) {
                  // se trovo uno smart che è pianificato per risorsa vado a capire se devo creare la Practice/Discipline e la FpGeo relativa
                  if (
                    !gridRows.find(
                      (row) =>
                        row.type == "resource" &&
                        row.resourceId == currentSmart.resource.id,
                    )
                  ) {
                    createResource = true;
                    createArea =
                      gridRows.findIndex(
                        (row) => row.disciplineId == currentSmart.discipline.id,
                      ) == -1;
                    createFigure = !gridRows.find(
                      (row) =>
                        row.type == "figure" &&
                        row.figureId == currentSmart.professionalFigureId &&
                        row.geoId == currentSmart.geoId &&
                        currentSmart.discipline.id == row.disciplineId,
                    );
                  }
                } else if (!!currentSmart.professionalFigure) {
                  // Se trovo uno smart pianificato per FpGeo vado a capire se devo creare la Practice/Discipline relativa
                  createArea =
                    gridRows.findIndex(
                      (row) =>
                        row.type.includes("area") &&
                        row.disciplineId == currentSmart.discipline.id,
                    ) == -1;
                  createFigure = !gridRows.find(
                    (row) =>
                      row.type == "figure" &&
                      row.figureId == currentSmart.professionalFigureId &&
                      row.geoId == currentSmart.geoId &&
                      currentSmart.discipline.id == row.disciplineId &&
                      !row.isPep,
                  );
                  if (createFigure) {
                    // Estraggo tutti gli smart relativi a questa figure
                    figureSmarts = SMARTS.filter(
                      (smart) =>
                        smart.professionalFigureId + " " + smart.geoId ==
                          currentSmart.professionalFigureId +
                            " " +
                            currentSmart.geoId &&
                        currentSmart.discipline.id === smart.discipline.id,
                    )
                      .sort((sm1, sm2) => (sm1.week.id > sm2.week.id ? 1 : -1))
                      .map(
                        (smart) =>
                          ({
                            week: smart.week,
                            weeklyProposalHours:
                              smart.weeklyProposalScheduledHours,
                            scheduledHours: smart.numberOfHours,
                            weeklyTheoreticalHours:
                              smart.weeklyTheoreticalHours,
                            weeklyContractHours:
                              smart.weeklyContractScheduledHours,
                            isEdited: false,
                          }) as SmartCell,
                      );
                  }
                } else if (!!currentSmart.discipline) {
                  // Vado a vedere se l'area esiste e in caso contrario estraggo i suoi smart e la vado a creare
                  createArea =
                    gridRows.findIndex(
                      (row) =>
                        row.type == "area" &&
                        row.disciplineId == currentSmart.discipline.id,
                    ) == -1;
                  areaSmarts = SMARTS.filter(
                    (smart) =>
                      smart.discipline.id == currentSmart.discipline.id,
                  )
                    .sort((sm1, sm2) => (sm1.week.id > sm2.week.id ? 1 : -1))
                    .map(
                      (smart) =>
                        ({
                          week: smart.week,
                          weeklyProposalHours:
                            smart.weeklyProposalScheduledHours,
                          scheduledHours: smart.numberOfHours,
                          weeklyTheoreticalHours: smart.weeklyTheoreticalHours,
                          weeklyContractHours:
                            smart.weeklyContractScheduledHours,
                          isEdited: false,
                        }) as SmartCell,
                    );
                }

                if (createArea) {
                  let mappedSmarts: SmartCell[] = [];
                  let middleTotalsSmart =
                    response.agreement.middleTotals.filter(
                      (mt) =>
                        mt.disciplineId == currentSmart.discipline.id &&
                        mt.professionalFigureId == null &&
                        mt.geoId == null &&
                        filteredWeeksId.includes(mt.weekId),
                    );

                  if (areaSmarts.length == 0 && middleTotalsSmart.length > 0) {
                    mappedSmarts = middleTotalsSmart.map((totals) => ({
                      isEdited: false,
                      weeklyContractHours: totals.weeklyContractScheduledHours,
                      scheduledHours: 0,
                      weeklyProposalHours: totals.weeklyProposalScheduledHours,
                      weeklyTheoreticalHours: totals.weeklyTheoreticalHours,
                      week: weeksOfProject.find((w) => w.id == totals.weekId)!,
                    }));
                  } else if (
                    areaSmarts.length == 0 &&
                    middleTotalsSmart.length == 0
                  ) {
                    if (isPreStaffingOrContract) {
                      let today = moment.utc();
                      today.subtract(2, "weeks");
                      today.subtract(1, "day")
                      let mondayOfTwoWeeksAgo =
                        QuartersEngine.findMondayMoment(today);
                      let weeksFromPastTwoWeeks =
                        response.agreement.weeks.filter((w) =>
                          moment
                            .utc(w.dateStart)
                            .isSameOrAfter(mondayOfTwoWeeksAgo),
                        );
                      mappedSmarts = weeksFromPastTwoWeeks.map((w) => ({
                        isEdited: false,
                        scheduledHours: 0,
                        week: w,
                        weeklyContractHours: 0,
                        weeklyProposalHours: 0,
                        weeklyTheoreticalHours: 0,
                      }));
                    }
                  } else {
                    mappedSmarts = areaSmarts;
                  }

                  mappedSmarts.filter(
                    (sc) =>
                      sc.week.dateStart >=
                      today.utc(false).format("YYYY-MM-DD"),
                  );

                  gridRows.push({
                    type: "area",
                    area:
                      currentSmart.discipline.practice.description +
                      "/" +
                      currentSmart.discipline.description,
                    disciplineId: currentSmart.discipline.id,
                    practiceId: currentSmart.discipline.practice.id,
                    figureId: "",
                    figure: "",
                    geoId: "",
                    smarts: mappedSmarts,
                    readonly: !(areaSmarts.length > 0),
                    hc: 0,
                    resource: "",
                    hours: { scheduled: 0, tot: 0, consumed: 0, remaining: 0 },
                    isPep: false,
                  });
                  createArea = false;
                }
                if (createFigure) {
                  let mappedSmarts: SmartCell[] = [];
                  let middleTotalsSmart =
                    response.agreement.middleTotals.filter(
                      (mt) =>
                        mt.disciplineId == currentSmart.discipline.id &&
                        mt.professionalFigureId ==
                          currentSmart.professionalFigureId &&
                        mt.geoId == currentSmart.geoId &&
                        filteredWeeksId.includes(mt.weekId),
                    );

                  if (
                    figureSmarts.length == 0 &&
                    middleTotalsSmart.length > 0
                  ) {
                    mappedSmarts = middleTotalsSmart.map((totals) => ({
                      isEdited: false,
                      weeklyContractHours: totals.weeklyContractScheduledHours,
                      scheduledHours: 0,
                      weeklyProposalHours: totals.weeklyProposalScheduledHours,
                      weeklyTheoreticalHours: totals.weeklyTheoreticalHours,
                      week: weeksOfProject.find((w) => w.id == totals.weekId)!,
                    }));
                  } else if (
                    figureSmarts.length == 0 &&
                    middleTotalsSmart.length == 0
                  ) {
                    if (isPreStaffingOrContract) {

                      let today = moment.utc();
                      today.subtract(2, "weeks");
                      today.subtract(1, "day")
                      let mondayOfTwoWeeksAgo =
                        QuartersEngine.findMondayMoment(today);
                      let weeksFromPastTwoWeeks =
                        response.agreement.weeks.filter((w) =>
                          moment
                            .utc(w.dateStart)
                            .isSameOrAfter(mondayOfTwoWeeksAgo),
                        );
                      mappedSmarts = weeksFromPastTwoWeeks.map((w) => ({
                        isEdited: false,
                        scheduledHours: 0,
                        week: w,
                        weeklyContractHours: 0,
                        weeklyProposalHours: 0,
                        weeklyTheoreticalHours: 0,
                      }));
                    }
                  } else {
                    mappedSmarts = figureSmarts;
                  }

                  mappedSmarts.filter(
                    (sc) =>
                      sc.week.dateStart >=
                      today.utc(false).format("YYYY-MM-DD"),
                  );

                  let areaIndex = gridRows.findIndex(
                    (row) =>
                      row.type.includes("area") &&
                      row.disciplineId == currentSmart.discipline.id,
                  );
                  let indexToGetFigureFrom = gridRows
                    .map((row) => row.type + "#" + row.disciplineId)
                    .lastIndexOf("figure#" + currentSmart.discipline.id);
                  let numberOfResourceOfFigureToGetDataFrom =
                    indexToGetFigureFrom != -1
                      ? gridRows.filter(
                          (row) =>
                            row.type == "resource" &&
                            row.disciplineId ==
                              gridRows[indexToGetFigureFrom].disciplineId &&
                            row.figureId ==
                              gridRows[indexToGetFigureFrom].figureId &&
                            row.geoId == gridRows[indexToGetFigureFrom].geoId,
                        ).length
                      : 0;
                  gridRows.splice(
                    (indexToGetFigureFrom != -1
                      ? indexToGetFigureFrom
                      : areaIndex) +
                      numberOfResourceOfFigureToGetDataFrom +
                      1,
                    0,
                    {
                      ...gridRows[areaIndex],
                      type: "figure",
                      geoId: currentSmart.geoId,
                      figureId: currentSmart.professionalFigureId,
                      figure:
                        currentSmart.professionalFigure?.description +
                        " " +
                        currentSmart.geoId,
                      smarts: mappedSmarts,
                      readonly: !(figureSmarts.length > 0),
                      hours: {
                        // tot: getHourAssignment(HOURASSIGNMENTFPGEO, currentSmart.professionalFigureId, currentSmart.geoId).numberOfHours,
                        tot: 0,
                        scheduled: 0,
                        consumed: 0,
                        remaining: 0,
                      },
                      isPep: false,
                    },
                  );
                  createFigure = false;
                }
                if (createResource) {
                  let indexToGetFigureFrom = gridRows.findIndex(
                    (row) =>
                      row.type == "figure" &&
                      row.disciplineId == currentSmart.discipline.id &&
                      row.figureId == currentSmart.professionalFigureId &&
                      row.geoId == currentSmart.geoId,
                  );
                  let numberOfResourceOfFigureToGetDataFrom = gridRows.filter(
                    (row) =>
                      row.type == "resource" &&
                      row.disciplineId ==
                        gridRows[indexToGetFigureFrom].disciplineId &&
                      row.figureId == gridRows[indexToGetFigureFrom].figureId &&
                      row.geoId == gridRows[indexToGetFigureFrom].geoId,
                  ).length;

                  gridRows.splice(
                    indexToGetFigureFrom +
                      numberOfResourceOfFigureToGetDataFrom +
                      1,
                    0,
                    {
                      ...gridRows[indexToGetFigureFrom],
                      hours: {
                        scheduled: 0,
                        tot: 0,
                        consumed: 0,
                        remaining: 0,
                      },
                      type: "resource",
                      resource:
                        currentSmart.resource.name +
                        " " +
                        currentSmart.resource.surname,
                      resourceId: currentSmart.resource.id,
                      readonly: false,
                      isPep: false,
                      smarts: SMARTS.filter(
                        (smart) =>
                          smart.resource?.id == currentSmart.resource.id &&
                          smart.week.dateStart >=
                            today.utc(false).format("YYYY-MM-DD"),
                      )
                        .sort((sm1, sm2) =>
                          sm1.week.id > sm2.week.id ? 1 : -1,
                        )
                        .map(
                          (smart) =>
                            ({
                              week: smart.week,
                              weeklyProposalHours:
                                smart.weeklyProposalScheduledHours ?? 0,
                              scheduledHours: smart.numberOfHours,
                              weeklyTheoreticalHours:
                                smart.weeklyTheoreticalHours ?? 0,
                              weeklyContractHours:
                                smart.weeklyContractScheduledHours ?? 0,
                              isEdited: false,
                            }) as SmartCell,
                        ),
                    },
                  );
                  createResource = false;
                }

                let checkCurrSmartString = `${currentSmart.resource?.id ?? ""}#${currentSmart.professionalFigure?.id ?? ""}|${currentSmart.geoId ?? ""}#${currentSmart.discipline.id}`;

                SMARTS = [
                  ...SMARTS.filter((smart) => {
                    let checkSmartString = `${smart.resource?.id ?? ""}#${smart.professionalFigure?.id ?? ""}|${smart.geoId ?? ""}#${smart.discipline.id}`;
                    return checkCurrSmartString != checkSmartString;
                  }),
                ];
              }

              // CALCOLO SMART CELL e Hours per area e figure delle risorse
              let ownerSmartMap = new Map<string, SmartCell[]>();
              if (isPreStaffingOrContract) {
                const figureRows = gridRows.filter(
                  (row) => row.type == "figure",
                );
                figureRows.forEach((figureRow) =>
                  ownerSmartMap.set(
                    figureRow.figureId +
                      "#" +
                      figureRow.geoId +
                      "#" +
                      figureRow.disciplineId,
                    gridRows
                      .filter(
                        (row) =>
                          row.type == "resource" &&
                          row.figureId == figureRow.figureId &&
                          row.disciplineId == figureRow.disciplineId &&
                          row.figureId == figureRow.figureId &&
                          row.geoId == figureRow.geoId,
                      )
                      .flatMap((row) => row.smarts),
                  ),
                );
                ownerSmartMap.forEach((smartCells, smartOwner) => {
                  if (smartCells && smartCells.length > 0) {
                    const aggregatedMap = sumSmarts([...smartCells]);

                    ownerSmartMap.set(
                      smartOwner,
                      Array.from(aggregatedMap.values()),
                    );
                    const splittedOwner = smartOwner.split("#");
                    let figureId: string = splittedOwner[0];
                    let geoId: string = splittedOwner[1];
                    let disciplineId: number = Number(splittedOwner[2]);
                    if (disciplineId && geoId && figureId) {
                      gridRows
                        .find(
                          (row) =>
                            row.figureId == figureId &&
                            row.geoId == geoId &&
                            row.disciplineId == disciplineId,
                      )!
                      .smarts.forEach((smart) => {
                        let summedSmarts = [
                          ...Array.from(aggregatedMap.values()),
                        ];
                        smart.scheduledHours =
                          summedSmarts.find(
                            (sumSm) =>
                              sumSm.week &&
                              smart.week &&
                              sumSm.week.id == smart.week.id,
                          )?.scheduledHours ?? 0;
                      });
                    }
                  } else
                    ownerSmartMap.set(
                      smartOwner,
                      gridRows
                        .filter(
                          (row) =>
                            row.type == "figure" &&
                            row.figureId == smartOwner.split("#")[0] &&
                            row.geoId == smartOwner.split("#")[1] &&
                            row.disciplineId?.toString() ==
                              smartOwner.split("#")[2],
                        )
                        .flatMap((row) => row.smarts),
                    );
                });
              } else {
                const areaRows = gridRows.filter((row) =>
                  row.type.includes("area"),
                );
                areaRows.forEach((areaRow) =>
                  ownerSmartMap.set(
                    areaRow.disciplineId!.toString(),
                    gridRows
                      .filter(
                        (row) =>
                          row.type == "figure" &&
                          row.disciplineId == areaRow.disciplineId,
                      )
                      .flatMap((row) => row.smarts),
                  ),
                );
              }

              ownerSmartMap.forEach((cells, owner) => {
                let disciplineId: number = owner.includes("#")
                  ? Number(owner.split("#")[2])
                  : Number(owner);
                let areaOwner = gridRows.find(
                  (row) =>
                    row.type.includes("area") &&
                    row.disciplineId == disciplineId,
                );
                if (areaOwner)
                  areaOwner.smarts = Array.from(
                    sumSmarts([...areaOwner.smarts, ...cells]).values(),
                  );
              });

              calculateConsumedHours(gridRows, historicalSmarts);

              gridRows.forEach((row, index) => {
                let sum = 0;
                for (let i = 0; i < row.smarts.length; i++) {
                  const curr = row.smarts[i];
                  if (
                    moment(curr.week.dateStart)
                      .utc(true)
                      .isSameOrAfter(
                        QuartersEngine.findMondayMoment(moment().utc(true).set({hours:0, minutes:0, seconds: 0, milliseconds: 0})),
                      )
                  ) {
                    sum = sum + curr.scheduledHours;
                  }
                }
                row.hours.scheduled = sum;
              });
              gridRows.forEach((row, index) => {
                row.hours.remaining =
                  row.type == "resource"
                    ? historicalSmarts.find(
                        (x) =>
                          x.discipline?.id == row.disciplineId &&
                          x.geoId == row.geoId &&
                          x.professionalFigureId == row.figureId &&
                          x.resourceId == row.resourceId,
                      )?.nch
                    : row.type == "figure"
                      ? historicalSmarts
                          .filter(
                            (x) =>
                              x.discipline?.id == row.disciplineId &&
                              x.geoId == row.geoId &&
                              x.professionalFigureId == row.figureId,
                          )
                          ?.reduce((acc, smart) => acc + smart.nch, 0)
                      : //row.type == "area"
                        historicalSmarts
                          .filter((x) => x.discipline?.id == row.disciplineId)
                          ?.reduce((acc, smart) => acc + smart.nch, 0);
              });
            }

            let agreementHeader: AgreementHeader = {
              clm: {
                externalSource: document.externalSource,
                externalSourceDesc: document.externalSourceDesc,
              },
              oppportunityCode: workload.opportunityCode,
              externalCode: workload.externalCode,
              owner:
                "contractOwner" in document
                  ? document.contractOwner
                  : "offerOwner" in document
                    ? document.offerOwner
                    : "-",
              ownerId:
                  "contractOwnerId" in document
                    ? document.contractOwnerId
                    : undefined,
              customer:
                "contractualCustomerName" in document
                  ? document.contractualCustomerName
                  : "customer" in document
                    ? document.customer
                    : "",
              endDate: moment(
                "dateEnd" in document
                  ? document.dateEnd
                  : "offerClosureDate" in document
                    ? document.offerClosureDate
                    : "contractEndDate" in document
                      ? document.contractEndDate
                      : "",
              ),
              startDate: moment(
                "dateStart" in document
                  ? document.dateStart
                  : "offerStartDate" in document
                    ? document.offerStartDate
                    : "contractStartDate" in document
                      ? document.contractStartDate
                      : "",
              ),
              smartStartDate: moment(response.agreement.dateStart),
              smartEndDate: moment(response.agreement.dateEnd),
              notes: response.agreement.note ?? "",
              projectEngineer: {
                id: pepResourceId,
                fullname: pepResourceFullname,
              },
              winProbability:
                "winProbability" in document ? document.winProbability * 10 : 0,
              code:
                "contractCode" in document
                  ? document.contractCode!
                  : "offerCode" in document
                    ? document.offerCode
                    : workload.opportunityCode,
              name:
                "contractName" in document
                  ? document.contractName!
                  : "offerName" in document
                    ? document.offerName
                    : document.name,
              status:
                "statusDesc" in document ? document.statusDesc : undefined,
              workloadId: response.agreement.workload?.id,
              proposalId: response.agreement.proposal?.id,
              contractId: response.agreement.contract?.id,
              quoteAmountLabs: response.agreement.proposal?.quoteAmountLabs,
            };

            let agreementDashboard: AgreementDashboard = {
              agreementHeader: agreementHeader,
              clmHeader: { name: "", status: "", code: "" },
              professionalProfilesHeader: HOURASSIGNMENTFPGEO.map(
                (hafpgeo) => ({
                  professionalFigureDesc:
                    hafpgeo.professionalFigure?.description,
                    figureId: hafpgeo.professionalFigure?.id,
                    geoId: hafpgeo.geoId,
                    scheduledHours: hafpgeo.numberOfHours,
                    consumedHours: 0,
                }),
              ),
              finalServices: response.agreement.finalServices,
              forecasts: forecasts,
              quoteAmountLabs: quoteAmountLabs,
              agreementStatus:
                "status" in document ? document.status : undefined,
              agreementStatusDesc:
                "statusDesc" in document ? document.statusDesc : undefined,
            };

            return {
              records: gridRows,
              agreementHeader,
              agreementDashboard,
              agreementId: response.agreement.id,
              historicalSmarts,
              costFreeze,
              quoteExpenses: response.agreement.forecasts,
              weeksOfProject,
              practiceDisciplineCosts: response.agreement.practiceDisciplineCosts
            } as AgreementData;
          } catch (e) {
            showNotification(
              "System Error - Please contact Support Team",
              "error",
            );
            console.log(e);
            this.router
              .navigate(["redirect"], { skipLocationChange: true })
              .then((x) => this.router.navigate([""]));
          }

          return {} as AgreementData;
        }),
      );
  }

  override changeStatus(agreementId: number): Observable<string> {
    return this.http
      .get<{
        response: boolean;
      }>(this.endpoint + `/sm/agreement/${agreementId}/status`)
      .pipe(map((res) => "success"));
  }

  override createWorkloadFromOpportunity(
    agreementId: number,
    wizardValue: PlanningWizardDTO,
  ): Observable<boolean> {
    return this.http
      .put<{
        response: boolean;
      }>(this.endpoint + `/sm/agreement/${agreementId}/wizard`, {
        wizardDto: wizardValue,
      })
      .pipe(map((res) => res.response));
    // return of(false);
  }

  override publishOpportunity(
    agreementId: number,
    // smartsToCreate: SaveAgreementDTO[],
  ): Observable<boolean> {
    return this.http
      .get<any>(`${this.endpoint}/sm/agreement/publish/${agreementId}`)
      .pipe(map((res) => (res ? true : false)));
  }
}

function calculateConsumedHours(
  gridRows: GridRow[],
  historicalSmarts: HistoricalSmarts[],
) {
  // Create lookup maps for figures and areas
  const figureMap = new Map<string, GridRow>();
  const areaMap = new Map<string, GridRow>();

  gridRows.forEach((row) => {
    if (row.type === "figure") {
      const key = `${row?.area}|${row?.figureId}|${row?.geoId}`;
      figureMap.set(key, row);
    } else if (row.type === "area") {
      areaMap.set(row.area, row);
    }
    // Initialize consumed hours to 0 for all rows
    if (!row.hours.consumed) row.hours.consumed = 0;
  });

  // Update consumed hours for resources, figures, and areas
  gridRows.forEach((row) => {
    if (row.type === "resource" && row.resourceId) {
      // Get consumed hours from pre-aggregated map
      const consumedHours = Math.floor(
        historicalSmarts.find((x) => x.resourceId == row.resourceId)
          ?.sumHours || 0,
      );
      row.hours.consumed = consumedHours;

      // Update corresponding figure
      const figureKey = `${row?.area}|${row?.figureId}|${row?.geoId}`;
      const correspondingFigure = figureMap.get(figureKey);
      if (correspondingFigure) {
        correspondingFigure.hours.consumed += consumedHours;
      }

      // Update corresponding area
      const correspondingArea = areaMap.get(row.area);
      if (correspondingArea) {
        correspondingArea.hours.consumed += consumedHours;
      }
    }
  });
}

function sumSmarts(smartCells: SmartCell[]): Map<number, SmartCell> {
  const aggregatedMap = new Map<number, SmartCell>();
  smartCells.forEach((cell) => {
    const weekId = cell.week.id;
    if (!aggregatedMap.has(weekId)) {
      aggregatedMap.set(weekId, { ...cell });
    } else {
      let existingCell = aggregatedMap.get(weekId)!;
      aggregatedMap.set(weekId, {
        ...existingCell,
        scheduledHours: existingCell.scheduledHours + cell.scheduledHours,
      });
    }
  });
  return aggregatedMap;
}
